import React, { useState, useEffect } from "react";
import axios from "axios";
import "./FIR.css";
import FIRWizard from "./FIRWizard";
import { buildVisibleSequence } from "./firVisibility";
import { REGION_BUREAUX, LEGACY_BUREAU_FIELDS } from "./regionBureaux";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// Same creation/legacy filtering rules as the wizard so completion % matches.
const LEGACY_BUREAU_SET = new Set(LEGACY_BUREAU_FIELDS);
const CREATION_FIELDS = new Set(["Nom", "Pr_nom", "Date_de_naissance1"]);

// Format duration (ms) → human readable French
function formatDuration(ms) {
  if (!ms || ms < 0) return "—";
  const sec = Math.floor(ms / 1000);
  const m = Math.floor(sec / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}j ${h % 24}h`;
  if (h > 0) return `${h}h ${m % 60}min`;
  if (m > 0) return `${m}min ${sec % 60}s`;
  return `${sec}s`;
}

function buildPatchedSchema(schema, answers) {
  if (!schema) return null;
  return {
    ...schema,
    sections: schema.sections.map((sec, idx) => {
      let fields = sec.fields.filter(f => !CREATION_FIELDS.has(f.name));
      if (idx === 0) {
        fields = fields.filter(f => !LEGACY_BUREAU_SET.has(f.name));
        const region = answers._region || "";
        const bureauOptions = region ? (REGION_BUREAUX[region] || []) : [];
        fields = [
          { name: "_region", type: "picklist", values: Object.keys(REGION_BUREAUX).sort() },
          { name: "_bureau", type: "picklist", values: bureauOptions },
          ...fields,
        ];
      }
      return { ...sec, fields };
    })
  };
}

function computeCompletion(schema, answers) {
  if (!schema) return { answered: 0, total: 0, pct: 0 };
  const patched = buildPatchedSchema(schema, answers);
  const seq = buildVisibleSequence(patched, answers);
  const fields = seq.filter(s => s.type === "field").map(s => s.field);
  const total = fields.length;
  let answered = 0;
  for (const f of fields) {
    const v = answers[f.name];
    if (v == null) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    answered++;
  }
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0;
  return { answered, total, pct };
}

export default function FIRModal({ open, onClose, initialFirId }) {
  const [items, setItems] = useState([]);
  const [schema, setSchema] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("in_progress");
  const [loading, setLoading] = useState(false);
  const [activeFirId, setActiveFirId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.q = search;
      const res = await axios.get(`${API}/fir`, { params });
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, statusFilter]);

  // Load schema once when modal opens (used for completion % computation)
  useEffect(() => {
    if (!open || schema) return;
    axios.get(`${API}/fir/schema`).then(res => setSchema(res.data)).catch(() => {});
  }, [open, schema]);

  // Mode Entrevue (Nouvelle demande) : ouvrir directement l'entrevue créée.
  useEffect(() => {
    if (open && initialFirId) setActiveFirId(initialFirId);
  }, [open, initialFirId]);

  if (!open) return null;

  if (activeFirId) {
    return (
      <FIRWizard
        firId={activeFirId}
        onClose={() => { setActiveFirId(null); reload(); }}
        onCompleted={() => { setActiveFirId(null); setStatusFilter("completed"); reload(); }}
      />
    );
  }

  const removeFir = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer cette entrevue ?")) return;
    await axios.delete(`${API}/fir/${id}`);
    reload();
  };

  // Créer une DAJ pré-remplie depuis les réponses d'une FIR.
  const creerDaj = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(
        "Créer une nouvelle demande d'aide juridique à partir de cette entrevue ?\n\n" +
        "Les réponses seront pré-remplies dans la nouvelle DAJ."))
      return;
    try {
      const res = await axios.post(`${API}/fir/${id}/creer-daj`);
      const data = res.data || {};
      const clientLine = data.client_created
        ? `Nouveau client créé : ${data.noclient}`
        : (data.noclient
            ? `Client existant réutilisé : ${data.noclient}`
            : "Aucun client lié (données insuffisantes)");
      alert(
        `✓ DAJ créée avec succès !\n\n` +
        `pk_daj    : ${data.pk_daj}\n` +
        `noseqdaj  : ${data.noseqdaj}\n` +
        `${clientLine}\n` +
        `Requérants secondaires créés (conjoint/enfants) : ${data.multirequerants_created ?? 0}\n` +
        `Champs pré-remplis : ${data.prefilled_count} (${(data.prefilled_columns || []).join(", ")})`
      );
      reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || String(err);
      alert("Erreur lors de la création de la DAJ :\n" + detail);
    }
  };

  return (
    <div className="fir-overlay">{/* fermeture uniquement via le bouton × */}
      <div className="fir-modal" style={{ height: '78vh' }}>
        <div className="fir-titlebar">
          <span>FIR — Fiche Initiale de Renseignements</span>
          <button onClick={onClose} title="Fermer" data-testid="fir-modal-close">×</button>
        </div>

        <div className="fir-tabs">
          <div className="fir-tab active" data-testid="fir-tab-list">Entrevues</div>
        </div>

        <div className="fir-body">
          <div style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'center' }}>
            <label>Statut :</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="fir-input"
              style={{ width: 160 }}
              data-testid="fir-status-filter"
            >
              <option value="">Tous</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Terminées</option>
              <option value="converted">Converties en DAJ</option>
            </select>
            <input
              className="fir-search"
              placeholder="Rechercher (nom, prénom, titre)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') reload(); }}
              data-testid="fir-search-input"
            />
            <button className="win-btn" onClick={reload} data-testid="fir-search-btn">Rechercher</button>
            <span style={{ marginLeft: 'auto', color: '#666' }}>{items.length} résultat(s)</span>
          </div>

          <div className="fir-list-row header">
            <div>#</div>
            <div>Titre</div>
            <div>Client</div>
            <div>Statut</div>
            <div>Temps</div>
            <div>Complétion</div>
            <div>Mise à jour</div>
            <div>Actions</div>
          </div>

          {loading && <div className="fir-empty">Chargement…</div>}
          {!loading && items.length === 0 && (
            <div className="fir-empty">
              Aucune entrevue {statusFilter === "in_progress" ? "en cours" : ""}.
              <br/><br/>
              Les entrevues se créent depuis <b>Nouveau → Entrevue</b>.
            </div>
          )}
          {!loading && items.map(it => {
            const elapsed = (it.total_seconds || 0) * 1000;
            const comp = computeCompletion(schema, it.answers || {});
            const updated = it.updated_at ? new Date(it.updated_at) : null;
            const selectionne = !!(it.answers && it.answers._selectionne);
            return (
              <div
                key={it.id}
                className="fir-list-row"
                onClick={() => setActiveFirId(it.id)}
                data-testid={`fir-row-${it.id}`}
              >
                <div>{it.id}</div>
                <div>{it.titre || "—"}</div>
                <div>{[it.client_prenom, it.client_nom].filter(Boolean).join(" ") || "—"}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span className={`fir-status-badge ${it.status}`}>{
                    it.status === "in_progress" ? "En cours" :
                    it.status === "completed" ? "Terminée" :
                    it.status === "converted" ? "Convertie" : it.status
                  }</span>
                  {selectionne && (
                    <span
                      className="fir-status-badge"
                      style={{ background: '#c0392b', color: '#fff', fontWeight: 700 }}
                      title="Le client a été sélectionné pour apporter la documentation"
                      data-testid={`fir-selectionne-${it.id}`}
                    >📄 Sélectionné — documentation</span>
                  )}
                </div>
                <div data-testid={`fir-elapsed-${it.id}`}>{formatDuration(elapsed)}</div>
                <div className="fir-progress-cell" data-testid={`fir-completion-${it.id}`}>
                  <div className="fir-progress-mini">
                    <div style={{ width: `${comp.pct}%` }} />
                  </div>
                  <span className="pct">{comp.pct}%</span>
                </div>
                <div style={{ fontSize: 11, color: '#666' }}>
                  {updated ? updated.toLocaleString('fr-CA') : '—'}
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {it.status !== "converted" && (
                    <button
                      className="win-btn-blue"
                      onClick={(e) => creerDaj(it.id, e)}
                      style={{ fontSize: 11 }}
                      title="Créer une nouvelle demande d'aide juridique pré-remplie avec les réponses de cette entrevue"
                      data-testid={`fir-creer-daj-${it.id}`}
                    >Créer DAJ</button>
                  )}
                  <button
                    className="win-btn"
                    onClick={(e) => removeFir(it.id, e)}
                    style={{ fontSize: 11 }}
                    data-testid={`fir-delete-${it.id}`}
                  >Supprimer</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
